// web/src/screens/ForcedPinChangeScreen.jsx
// Shown immediately after login when force_pin_change = true.
// Agent-assisted onboarding sets a temp PIN via WhatsApp — owner must change it on first login.
// Receives currentPin (the temp PIN they just logged in with) from App.jsx.

import { useState } from 'react';
import { apiClient } from '../api';

const C = {
  bg:      'var(--bg)',
  surface: 'var(--surface)',
  orange:  'var(--orange)',
  text:    'var(--text)',
  muted:   'var(--text-muted)',
  danger:  'var(--danger)',
};

function PinPad({ label, value, onChange }) {
  const handleKey = (key) => {
    if (key === 'DEL') return onChange(value.slice(0, -1));
    if (value.length < 4) onChange(value + key);
  };

  return (
    <div style={{ marginBottom: 28 }}>
      <label style={s.label}>{label}</label>
      <div style={s.pinDots}>
        {[0,1,2,3].map(i => (
          <div
            key={i}
            style={{ ...s.dot, background: value.length > i ? C.orange : C.bg }}
          />
        ))}
      </div>
      <div style={s.numpad}>
        {['1','2','3','4','5','6','7','8','9','','0','DEL'].map((key, i) => (
          <button
            key={i}
            type="button"
            style={{ ...s.numKey, opacity: key === '' ? 0 : 1 }}
            onClick={() => key && handleKey(key)}
            disabled={!key}
          >
            {key}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ForcedPinChangeScreen({ currentPin, onSuccess }) {
  const [newPin,     setNewPin]     = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error,      setError]      = useState('');
  const [loading,    setLoading]    = useState(false);

  const handleSubmit = async () => {
    setError('');
    if (newPin.length !== 4)        return setError('New PIN must be 4 digits');
    if (confirmPin.length !== 4)    return setError('Please confirm your new PIN');
    if (newPin !== confirmPin)      return setError('PINs do not match');
    if (newPin === currentPin)      return setError('New PIN must be different from your temporary PIN');

    setLoading(true);
    try {
      await apiClient.post('/auth/change-pin', {
        current_pin: currentPin,
        new_pin:     newPin,
      });
      onSuccess();
    } catch (err) {
      setError(err.data?.error || 'PIN change failed. Please try again.');
      setNewPin('');
      setConfirmPin('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.container}>
      <div style={s.card}>
        <h1 style={s.logo}>ServeIQ</h1>

        <div style={s.notice}>
          <div style={s.noticeTitle}>🔐 Set Your PIN</div>
          <p style={s.noticeBody}>
            Your account was set up by an agent. Choose a personal PIN that only you know.
          </p>
        </div>

        <PinPad
          label="New PIN"
          value={newPin}
          onChange={setNewPin}
        />

        <PinPad
          label="Confirm New PIN"
          value={confirmPin}
          onChange={setConfirmPin}
        />

        {error && <p style={s.error}>{error}</p>}

        <button
          style={{ ...s.btn, opacity: loading ? 0.7 : 1 }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'SAVING…' : 'SET MY PIN'}
        </button>
      </div>
    </div>
  );
}

const s = {
  container: { display: 'flex', alignItems: 'center', justifyContent: 'center',
               minHeight: '100vh', backgroundColor: C.bg },
  card:      { background: C.surface, borderRadius: 12, padding: 32,
               width: '100%', maxWidth: 360, textAlign: 'center' },
  logo:      { color: C.orange, fontSize: 36, fontWeight: 700,
               margin: '0 0 20px', letterSpacing: 2 },

  notice:     { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8,
                padding: '14px 16px', marginBottom: 28, textAlign: 'left' },
  noticeTitle:{ color: C.orange, fontSize: 13, fontWeight: 700, letterSpacing: 1, marginBottom: 6 },
  noticeBody: { color: C.muted, fontSize: 13, lineHeight: 1.5, margin: 0 },

  label:   { display: 'block', color: C.muted, fontSize: 12, fontWeight: 600,
             letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 },
  pinDots: { display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 16 },
  dot:     { width: 16, height: 16, borderRadius: '50%',
             border: `2px solid ${C.orange}`, transition: 'background 0.15s' },
  numpad:  { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 8 },
  numKey:  { padding: '16px 0', background: 'var(--surface-2)', border: 'none', borderRadius: 8,
             color: C.text, fontSize: 20, fontWeight: 600, cursor: 'pointer',
             fontFamily: "'Barlow Condensed', sans-serif" },

  error: { color: C.danger, fontSize: 13, marginBottom: 16 },
  btn:   { width: '100%', padding: '14px 0', background: C.orange, border: 'none',
           borderRadius: 8, color: '#fff', fontSize: 18, fontWeight: 700,
           cursor: 'pointer', letterSpacing: 1, fontFamily: "'Barlow Condensed', sans-serif" },
};
